import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { StatusChip } from "@/components/StatusChip";
import Clock from "@/components/Clock";
import { waLink, PRICES } from "@/lib/constants";
import { Users, Send, CheckCircle2, ArrowLeft, Timer, Tag, MessageCircle, Sparkles } from "lucide-react";

const Stat = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="flex items-center gap-4 p-5">
      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${color}`}>
        <Icon className="h-7 w-7" />
      </div>
      <div>
        <p className="text-3xl font-extrabold text-slate-800">{value ?? "—"}</p>
        <p className="text-sm text-slate-400">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const SubCountdown = ({ stats }) => {
  const status = stats.subscription_status;
  const active = stats.is_active;
  const days = stats.days_remaining;
  let title = "غير مفعّل", tone = "from-red-600 to-rose-700";
  if (active && status === "trial") { title = "فترة تجريبية"; tone = "from-amber-500 to-orange-600"; }
  else if (active && status === "active") { title = "اشتراك فعّال"; tone = "from-emerald-700 to-teal-600"; }
  return (
    <Card className={`bg-gradient-to-l ${tone} text-white`}>
      <CardContent className="flex items-center gap-4 p-5">
        <Timer className="h-9 w-9" />
        <div>
          <p className="text-sm text-white/80">{title}</p>
          <p className="text-2xl font-extrabold">
            {active ? `متبقٍ ${days} يوم` : "انتهى الاشتراك"}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const PricingDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <Button className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="pricing-button">
        <Tag className="h-4 w-4" /> أسعار الاشتراكات
      </Button>
    </DialogTrigger>
    <DialogContent className="text-right">
      <DialogHeader><DialogTitle>باقات الاشتراك في منصة معاكم</DialogTitle></DialogHeader>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border-2 border-emerald-100 p-4 text-center">
          <p className="font-bold text-slate-700">الفصل الدراسي</p>
          <p className="my-2 text-3xl font-extrabold text-emerald-800">{PRICES.semester} <span className="text-base">ريال</span></p>
          <a href={waLink(`أرغب بالاشتراك في منصة معاكم — باقة الفصل الدراسي (${PRICES.semester} ريال). اسم المدرسة: `)} target="_blank" rel="noreferrer">
            <Button className="mt-1 w-full gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="order-semester"><MessageCircle className="h-4 w-4" /> طلب عبر واتساب</Button>
          </a>
        </div>
        <div className="relative rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-4 text-center">
          <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white"><Sparkles className="h-3 w-3" /> الأوفر</span>
          <p className="font-bold text-slate-700">السنة الدراسية</p>
          <p className="my-2 text-3xl font-extrabold text-emerald-800">{PRICES.year} <span className="text-base">ريال</span></p>
          <a href={waLink(`أرغب بالاشتراك في منصة معاكم — باقة السنة الدراسية (${PRICES.year} ريال). اسم المدرسة: `)} target="_blank" rel="noreferrer">
            <Button className="mt-1 w-full gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="order-year"><MessageCircle className="h-4 w-4" /> طلب عبر واتساب</Button>
          </a>
        </div>
      </div>
      <p className="text-center text-sm text-slate-400">لطلب الاشتراك أو الاستفسار تواصل معنا واتساب: <span dir="ltr" className="font-mono">+966 53 700 3141</span></p>
    </DialogContent>
  </Dialog>
);

export default function CounselorDashboard() {
  const [stats, setStats] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/my/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">مرحباً، {user?.name}</h1>
          <p className="text-slate-400">{user?.school_name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Clock />
          <div className="flex items-center gap-2 text-sm text-slate-500">
            واتساب: <StatusChip status={stats.whatsapp_status || "disconnected"} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SubCountdown stats={stats} />
        <Stat icon={Users} label="عدد الطلاب" value={stats.students} color="bg-emerald-100 text-emerald-700" />
        <Stat icon={Send} label="إجمالي الرسائل" value={stats.total_messages} color="bg-sky-100 text-sky-700" />
        <Stat icon={CheckCircle2} label="تم الإرسال" value={stats.sent} color="bg-green-100 text-green-700" />
      </div>

      <Card className="bg-gradient-to-l from-emerald-800 to-teal-700 text-white">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div>
            <h3 className="text-xl font-bold">إرسال رسالة جديدة</h3>
            <p className="text-white/80">اختر الطلاب وأرسل رسالة واتساب لأولياء أمورهم بضغطة زر</p>
          </div>
          <Button onClick={() => navigate("/app/send")} data-testid="go-send-button"
            className="gap-2 bg-white text-emerald-900 hover:bg-white/90">
            إرسال رسالة <ArrowLeft className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div>
            <h3 className="text-lg font-bold text-slate-800">الاشتراكات والدعم</h3>
            <p className="text-sm text-slate-400">اطّلع على الباقات أو تواصل معنا لأي استفسار</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <PricingDialog />
            <a href={waLink("لدي استفسار حول منصة معاكم")} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-2 border-green-300 text-green-700 hover:bg-green-50" data-testid="contact-whatsapp-button">
                <MessageCircle className="h-4 w-4" /> تواصل واتساب
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
